import { generateQuoteProposalPageURL } from 'utils/generateQuoteProposalPageURL';
import { useDealBuilderContext } from 'modules/dealBuilder/context';
import { useSelector } from 'react-redux';
import { Account } from 'types';
import { collectVariablesForCreateProposal, FinanceProgramFormValues } from 'modules/desking';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { useMutation } from '@apollo/client';
import { useFormContext } from 'react-hook-form';
import { useNotifications } from 'modules/notification';
import {
  dealBuilderApi,
  NotificationInput,
  CreateDealBuilderQuoteProposalResponse,
  CreateDealBuilderQuoteProposalVariables,
  CreateDealBuilderProposalResponse,
  CreateDealBuilderProposalVariables,
} from 'modules/dealBuilder/api';
import { sendViaToTransport } from '../constants';
import { mapDealBuilderFormValuesToDesking } from '../lib';
import { DealBuilderFormValues } from '../types';
import { useDealProposalPreview } from './useDealProposalPreview';

export const useDealBuilderPreviewAndSubmit = () => {
  const account = useSelector<any, Account>(state => state.account);

  const {
    activeEntity,
    startNewQuote,
    sourceData: { vo },
    equipment: { selectedEquipmentItem },
  } = useDealBuilderContext();

  const { handlePreviewProposal, handlePreviewQuoteProposal, isPreviewLoading } = useDealProposalPreview();
  const { showNotification } = useNotifications();

  const { getValues } = useFormContext<DealBuilderFormValues>();

  const [createDealBuilderQuoteProposal, { loading: isQuoteProposalSubmitting }] = useMutation<
    CreateDealBuilderQuoteProposalResponse,
    CreateDealBuilderQuoteProposalVariables
  >(dealBuilderApi.createDealBuilderQuoteProposal);

  const [createDealBuilderProposal, { loading: isProposalSubmitting }] = useMutation<
    CreateDealBuilderProposalResponse,
    CreateDealBuilderProposalVariables
  >(dealBuilderApi.createDealBuilderProposal);

  const handlePreview = async () => {
    if (!!activeEntity?.proposal?.settings?.configuration?.pricing) {
      return handlePreviewProposal(
        activeEntity!,
        selectedEquipmentItem?.equipment?.id ?? null,
        getValues('reviewSend')
      );
    } else {
      return handlePreviewQuoteProposal(
        activeEntity!,
        selectedEquipmentItem?.equipment?.id ?? null,
        getValues('reviewSend').notesToBuyer ?? null
      );
    }
  };

  const handleCreateDealBuilderProposal = async () => {
    if (!!activeEntity?.proposal?.settings?.configuration?.pricing) {
      return handleSubmitProposal();
    } else {
      return handleSubmitQuoteProposal();
    }
  };

  const handleSubmitQuoteProposal = async () => {
    const { sendVia, sendEmailTo = [], ccEmails = [], notesToBuyer } = getValues('reviewSend');

    if (!sendEmailTo.length) {
      showNotification('At least one recipient email is required', { type: 'error' });
      return null;
    }

    const notification: NotificationInput = {
      transport: sendViaToTransport[sendVia],
      emails: sendEmailTo.map(({ email }) => email),
      ccEmails: ccEmails.map(({ email }) => email),
    };

    const { data } = await createDealBuilderQuoteProposal({
      variables: {
        proposalId: activeEntity?.proposal?.proposalId!,
        input: {
          isDraft: false,
          notification,
          notesToBuyer: notesToBuyer,
          voId: vo!.vendorOpportunityId,
          proposalSettings: {
            meta: {
              equipmentId: selectedEquipmentItem?.equipment?.id ?? null,
            },
          },
        },
      },
    });

    const quoteProposalId = data?.dealBuilder?.createDealBuilderQuoteProposal?.entity?.id;

    if (!quoteProposalId) return null;

    await startNewQuote();

    return generateQuoteProposalPageURL(quoteProposalId, account.dynamicsAccountId!);
  };

  const handleSubmitProposal = async () => {
    const { sendVia, sendEmailTo, ccEmails, notesToBuyer } = getValues('reviewSend');

    const savedFormData = activeEntity?.proposal?.settings?.configuration?.formData as FinanceProgramFormValues;

    const deskingFormData = mapDealBuilderFormValuesToDesking(
      { sendVia, sendEmailTo, ccEmails, notesToBuyer },
      savedFormData,
      selectedEquipmentItem?.equipment?.id!
    );

    const variables = collectVariablesForCreateProposal({
      isDraft: false,
      voId: vo!.vendorOpportunityId,
      pricing: activeEntity?.proposal?.settings?.configuration?.pricing,
      formData: deskingFormData,
      menuByTerms: JSON.parse(activeEntity?.proposal?.settings?.configuration?.builder ?? '{}'),
      productsConfigInMenu: JSON.parse(activeEntity?.proposal?.settings?.configuration?.productsConfig ?? '{}'),
      commonProductsConfiguration: JSON.parse(
        activeEntity?.proposal?.settings?.configuration?.commonProductsConfiguration ?? '{}'
      ),
    });

    const { data } = await createDealBuilderProposal({
      variables: {
        input: variables.input,
        proposalId: activeEntity?.proposal?.proposalId!,
      },
    });

    const proposalId = data?.dealBuilder?.createDealBuilderProposal?.id ?? null;

    if (!proposalId) return null;

    await startNewQuote();

    return generateProposalPageURL(proposalId, account.dynamicsAccountId!);
  };

  return {
    handlePreview,
    isPreviewLoading,
    handleCreateDealBuilderProposal,
    isSubmitLoading: isQuoteProposalSubmitting || isProposalSubmitting,
  };
};
